/**
 * Touch-path tracing for the Draw stage.
 *
 * Captures pointer positions as a sequence of points, renders them onto
 * a Canvas 2D context, and exposes a simple "did they trace something"
 * check used for MVP-level scoring. Proper stroke-matching against the
 * letter's canonical stroke_path_data is a Phase 2 upgrade.
 *
 * The API is framework-agnostic — just hand it a canvas and call
 * start/move/end from event handlers.
 */

export interface Point {
  x: number;
  y: number;
  t: number;
}

export interface TraceAttempt {
  points: Point[];
  boundingBox: { x: number; y: number; w: number; h: number };
  lengthPx: number;
  durationMs: number;
}

export class TraceCanvas {
  private ctx: CanvasRenderingContext2D;
  private points: Point[] = [];
  private started = false;
  private lastPoint: Point | null = null;

  constructor(
    public readonly canvas: HTMLCanvasElement,
    public readonly strokeColor = '#FF7A59',
    public readonly strokeWidth = 14,
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas unsupported');
    this.ctx = ctx;
  }

  /** Resize canvas backing store to match its CSS size for crisp drawing. */
  fitToDisplay() {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    this.ctx.scale(dpr, dpr);
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.strokeStyle = this.strokeColor;
    this.ctx.lineWidth = this.strokeWidth;
  }

  clear() {
    this.points = [];
    const rect = this.canvas.getBoundingClientRect();
    this.ctx.clearRect(0, 0, rect.width, rect.height);
    this.started = false;
    this.lastPoint = null;
  }

  startAt(x: number, y: number) {
    this.started = true;
    const p = { x, y, t: performance.now() };
    this.points = [p];
    this.lastPoint = p;
    this.ctx.beginPath();
    this.ctx.moveTo(x, y);
  }

  moveTo(x: number, y: number) {
    if (!this.started) return;
    const p = { x, y, t: performance.now() };
    this.points.push(p);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();
    this.ctx.beginPath();
    this.ctx.moveTo(x, y);
    this.lastPoint = p;
  }

  end(): TraceAttempt | null {
    if (!this.started || this.points.length < 2) {
      this.started = false;
      return null;
    }
    const xs = this.points.map((p) => p.x);
    const ys = this.points.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    let length = 0;
    for (let i = 1; i < this.points.length; i++) {
      length += dist(this.points[i - 1]!, this.points[i]!);
    }

    const first = this.points[0]!;
    const last = this.points[this.points.length - 1]!;

    this.started = false;

    return {
      points: this.points,
      boundingBox: { x: minX, y: minY, w: maxX - minX, h: maxY - minY },
      lengthPx: length,
      durationMs: last.t - first.t,
    };
  }
}

function dist(a: Point, b: Point): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Very lenient "did they actually try?" check.
 *
 * A trace is accepted when ALL of these hold:
 *   - the stroke took at least 200 ms (so a tap doesn't count),
 *   - at least ~100 px was drawn total, AND
 *   - the stroke's longer dimension covers at least 35% of the canvas's
 *     longer dimension.
 *
 * The last condition uses the EXTENT (max of width / height), NOT the
 * bounding-box area. A straight vertical line like ا has width ≈ 0 so
 * box area is tiny, but its height fills most of the canvas and that
 * should count. Previously 'ا' always failed the 20%-area check and
 * the stage felt broken.
 *
 * For older kids we could tighten this by requiring overlap with the
 * target letter's stroke path (stroke_path_data from D1), but for MVP
 * this is enough to reward effort without frustrating 5-year-olds.
 */
export function scoreTrace(
  attempt: TraceAttempt,
  canvas: { width: number; height: number },
): 'ok' | 'too-short' | 'too-fast' {
  if (attempt.durationMs < 200) return 'too-fast';
  if (attempt.lengthPx < 100) return 'too-short';

  const canvasLong = Math.max(canvas.width, canvas.height);
  const boxLong = Math.max(attempt.boundingBox.w, attempt.boundingBox.h);
  if (boxLong < canvasLong * 0.35) return 'too-short';

  return 'ok';
}
